import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, RefreshCw, Save, Printer, FileText, Trash2, Share2,
  Check, Image, Loader2, ChevronDown, BookOpen, Eye, X, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API_URL = '';

export function NotesTab({ currentUser, getSubjectOptions, getYearOptions }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [genProgress, setGenProgress] = useState('');
  const [teacherClasses, setTeacherClasses] = useState([]);

  const [subject, setSubject] = useState('');
  const [yearLevel, setYearLevel] = useState(0);
  const [topic, setTopic] = useState('');
  const [customInstructions, setCustomInstructions] = useState('');

  const subjects = [...new Set(teacherClasses.map(c => c.subject))];
  const yearsForSubject = teacherClasses.filter(c => c.subject === subject).map(c => c.year).sort((a, b) => a - b);

  const loadTeacherClasses = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const res = await fetch(`${API_URL}/api/study-notes/teacher-classes?teacher_id=${currentUser.id}`);
      if (res.ok) {
        const data = await res.json();
        const classes = data.classes || [];
        setTeacherClasses(classes);
        if (classes.length > 0 && !subject) {
          setSubject(classes[0].subject);
          setYearLevel(classes[0].year);
        }
      }
    } catch (e) { console.error(e); }
  }, [currentUser?.id]);

  useEffect(() => {
    if (subject && teacherClasses.length > 0) {
      const matching = teacherClasses.filter(c => c.subject === subject);
      if (matching.length > 0 && !matching.some(c => c.year === yearLevel)) {
        setYearLevel(matching[0].year);
      }
    }
  }, [subject, teacherClasses]);

  const loadNotes = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/study-notes/list?teacher_id=${currentUser.id}`);
      if (res.ok) setNotes(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [currentUser?.id]);

  useEffect(() => { loadNotes(); loadTeacherClasses(); }, [loadNotes, loadTeacherClasses]);

  const handleGenerate = async () => {
    if (!topic.trim()) { toast.error('Please enter a topic'); return; }
    setGenerating(true);
    setGenProgress('Starting generation...');

    try {
      const res = await fetch(`${API_URL}/api/study-notes/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject, year_level: yearLevel, topic: topic.trim(),
          custom_instructions: customInstructions.trim() || null,
          teacher_id: currentUser.id,
        }),
      });

      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      const jobId = data.job_id;

      if (!jobId) {
        if (data.id) {
          await loadNotes();
          const noteRes = await fetch(`${API_URL}/api/study-notes/${data.id}`);
          if (noteRes.ok) setSelectedNote(await noteRes.json());
        }
        setGenerating(false);
        toast.success('Notes generated!');
        return;
      }

      // Poll for job completion
      const pollInterval = setInterval(async () => {
        try {
          const statusRes = await fetch(`${API_URL}/api/study-notes/generate/status/${jobId}`);
          if (!statusRes.ok) return;
          const statusData = await statusRes.json();
          setGenProgress(statusData.progress || statusData.status || 'Processing...');

          if (statusData.status === 'complete' && (statusData.result_id || statusData.note_id)) {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.success('Notes generated with AI diagrams!');
            await loadNotes();
            const noteId = statusData.result_id || statusData.note_id;
            const noteRes = await fetch(`${API_URL}/api/study-notes/${noteId}`);
            if (noteRes.ok) setSelectedNote(await noteRes.json());
          } else if (statusData.status === 'error') {
            clearInterval(pollInterval);
            setGenerating(false);
            toast.error(statusData.error || 'Generation failed');
          }
        } catch (e) { console.error(e); }
      }, 3000);

      // Safety timeout after 5 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        if (generating) {
          setGenerating(false);
          toast.error('Generation timed out. Please try again.');
        }
      }, 300000);

    } catch (e) {
      console.error(e);
      toast.error('Failed to generate notes');
      setGenerating(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    try {
      const res = await fetch(`${API_URL}/api/study-notes/${noteId}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Note deleted');
        if (selectedNote?.id === noteId) setSelectedNote(null);
        loadNotes();
      }
    } catch (e) { toast.error('Failed to delete'); }
  };

  const toggleShare = async (noteId) => {
    try {
      const res = await fetch(`${API_URL}/api/study-notes/${noteId}/share`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.shared ? 'Shared with students!' : 'Unshared from students');
        loadNotes();
        if (selectedNote?.id === noteId) {
          setSelectedNote(prev => ({ ...prev, shared: data.shared }));
        }
      }
    } catch (e) { toast.error('Failed to toggle share'); }
  };

  const handleDownloadPptx = async (noteId, title) => {
    try {
      const res = await fetch(`${API_URL}/api/study-notes/${noteId}/pptx`);
      if (!res.ok) throw new Error('Failed to download');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'study_notes'}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const printNote = () => {
    if (!selectedNote) return;
    const content = typeof selectedNote.content === 'string' ? JSON.parse(selectedNote.content) : selectedNote.content;
    const images = typeof selectedNote.images === 'string' ? JSON.parse(selectedNote.images) : (selectedNote.images || {});
    const sections = content.sections || [];
    const questions = content.practice_questions || [];

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>${content.title || selectedNote.title}</title>
    <style>
      body{font-family:'Segoe UI',Arial,sans-serif;max-width:850px;margin:0 auto;padding:40px 30px;color:#1a1a1a;line-height:1.7}
      h1{text-align:center;color:#fff;background:linear-gradient(135deg,#065f46,#0d9488);padding:18px 24px;border-radius:12px;font-size:21px;margin-bottom:8px}
      .subtitle{text-align:center;color:#6b7280;font-size:14px;margin-bottom:24px}
      .summary{background:#ecfdf5;border-radius:10px;padding:14px 18px;margin:14px 0 24px;border-left:4px solid #059669;font-style:italic;color:#374151}
      h2{color:#065f46;border-bottom:2px solid #d1fae5;padding-bottom:6px;margin-top:32px;font-size:17px}
      .content{margin:10px 0;text-align:justify;font-size:14px;color:#334155;white-space:pre-line}
      .key-terms{display:flex;flex-wrap:wrap;gap:8px;margin:12px 0}
      .key-term{background:#dbeafe;color:#1e40af;padding:4px 12px;border-radius:20px;font-size:12px;font-weight:600}
      .diagram{background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin:16px 0;text-align:center}
      .diagram img{max-width:100%;max-height:400px;border-radius:8px}
      .diagram .caption{font-size:12px;color:#6b7280;font-style:italic;margin-top:8px}
      .questions{background:#f9fafb;border-radius:10px;padding:16px 18px;margin:24px 0}
      .questions h2{border:none;margin-top:0}
      .q{margin:12px 0}
      .q-text{font-weight:600;font-size:14px}
      .a-text{color:#059669;font-size:13px;margin-top:4px;padding-left:16px;border-left:2px solid #d1fae5}
      .btn{background:#065f46;color:white;padding:10px 20px;border:none;border-radius:8px;cursor:pointer;margin-right:10px}
      .btn-close{background:#e2e8f0;color:#1e293b}
      .footer{text-align:center;color:#94a3b8;font-size:11px;margin-top:36px;border-top:2px solid #e2e8f0;padding-top:12px}
      @media print{.no-print{display:none!important}}
    </style></head><body>
    <div class="no-print" style="margin-bottom:20px"><button class="btn" onclick="window.print()">Print</button><button class="btn btn-close" onclick="window.close()">Close</button></div>
    <h1>${content.title || selectedNote.title}</h1>
    <div class="subtitle">${selectedNote.subject} — Year ${selectedNote.year_level}</div>
    ${content.summary ? '<div class="summary">' + content.summary + '</div>' : ''}
    ${sections.map((s, i) => '<h2>' + (s.heading || '') + '</h2><div class="content">' + (s.content || '') + '</div>' + (s.key_terms && s.key_terms.length > 0 ? '<div class="key-terms">' + s.key_terms.map(t => '<span class="key-term">' + t + '</span>').join('') + '</div>' : '') + (images[String(i)] ? '<div class="diagram"><img src="data:image/png;base64,' + images[String(i)] + '" alt="' + (s.image_description || 'Diagram') + '"/>' + (s.image_description ? '<div class="caption">Figure: ' + s.image_description + '</div>' : '') + '</div>' : '')).join('')}
    ${questions.length > 0 ? '<div class="questions"><h2>Practice Questions</h2>' + questions.map((q, j) => '<div class="q"><div class="q-text">Q' + (j+1) + '. ' + (q.question || '') + '</div><div class="a-text">A: ' + (q.answer || '') + '</div></div>').join('') + '</div>' : ''}
    <div class="footer">Generated ${new Date().toLocaleDateString()} | Penama Education Portal</div></body></html>`);
    printWindow.document.close();
  };

  const viewNote = async (noteId) => {
    try {
      const res = await fetch(`${API_URL}/api/study-notes/${noteId}`);
      if (res.ok) setSelectedNote(await res.json());
    } catch (e) { toast.error('Failed to load note'); }
  };

  // Parse content for display
  const parseContent = (note) => {
    if (!note) return { sections: [], questions: [], images: {} };
    const content = typeof note.content === 'string' ? JSON.parse(note.content) : (note.content || {});
    const images = typeof note.images === 'string' ? JSON.parse(note.images) : (note.images || {});
    return { ...content, images };
  };

  const noteContent = selectedNote ? parseContent(selectedNote) : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left column: Form + Notes List */}
      <div className="space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Image className="h-4 w-4 text-emerald-600" /> Generate Study Notes
            </CardTitle>
            <CardDescription className="text-xs">With AI diagrams & practice questions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Subject</Label>
              {subjects.length > 0 ? (
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="notes-select-subject" className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger data-testid="notes-select-subject" className="h-8 text-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {getSubjectOptions().map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Year Level</Label>
              {yearsForSubject.length > 0 ? (
                <Select value={String(yearLevel)} onValueChange={v => setYearLevel(parseInt(v))}>
                  <SelectTrigger data-testid="notes-select-year" className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearsForSubject.map(y => <SelectItem key={y} value={String(y)}>Year {y}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Select value={String(yearLevel)} onValueChange={v => setYearLevel(parseInt(v))}>
                  <SelectTrigger data-testid="notes-select-year" className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {getYearOptions().map(y => <SelectItem key={y} value={String(y)}>Year {y}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Topic</Label>
              <Input placeholder="e.g. Introduction to Fractions" value={topic} onChange={e => setTopic(e.target.value)} className="h-8 text-sm" data-testid="notes-topic-input" />
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Extra Instructions (optional)</Label>
              <Input placeholder="e.g. Focus on practical examples" value={customInstructions} onChange={e => setCustomInstructions(e.target.value)} className="h-8 text-sm" />
            </div>

            <Button className="w-full h-9" onClick={handleGenerate} disabled={generating || !topic.trim()} data-testid="generate-notes-btn">
              {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {genProgress}</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate Notes</>}
            </Button>
          </CardContent>
        </Card>

        {/* Notes List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><BookOpen className="h-4 w-4" /> My Notes ({notes.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
            {loading && <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>}
            {!loading && notes.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No notes yet. Generate one above!</p>}
            {notes.map(n => (
              <div key={n.id} className={`p-2 rounded-lg border cursor-pointer transition-all hover:shadow-sm ${selectedNote?.id === n.id ? 'bg-emerald-50 border-emerald-300' : 'bg-white hover:bg-gray-50'}`}
                onClick={() => viewNote(n.id)} data-testid={`note-item-${n.id}`}>
                <p className="text-xs font-medium truncate">{n.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] text-muted-foreground">{n.subject} Y{n.year_level}</span>
                  {n.shared && <Badge variant="outline" className="text-[10px] h-4 px-1 bg-green-50 text-green-700 border-green-200">Shared</Badge>}
                </div>
                <div className="flex gap-1 mt-1.5">
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px]" onClick={e => { e.stopPropagation(); toggleShare(n.id); }}
                    data-testid={`share-note-${n.id}`}>
                    {n.shared ? <Check className="h-3 w-3 mr-0.5 text-green-600" /> : <Share2 className="h-3 w-3 mr-0.5" />}
                    {n.shared ? 'Shared' : 'Share'}
                  </Button>
                  <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[10px] text-red-500" onClick={e => { e.stopPropagation(); handleDelete(n.id); }}
                    data-testid={`delete-note-${n.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Right column: Note Preview */}
      <div className="lg:col-span-3">
        <Card className="min-h-[500px]">
          <CardContent className="p-6">
            {generating && (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="relative">
                  <Loader2 className="h-10 w-10 animate-spin text-emerald-600" />
                  <Image className="h-4 w-4 absolute top-3 left-3 text-emerald-800" />
                </div>
                <p className="text-muted-foreground font-medium">{genProgress}</p>
                <p className="text-xs text-muted-foreground">Generating content with AI diagrams...</p>
              </div>
            )}

            {!generating && !selectedNote && (
              <div className="flex flex-col items-center justify-center h-64 gap-4 text-muted-foreground">
                <FileText className="h-12 w-12 opacity-30" />
                <p>Enter a topic and generate, or select a note from the list</p>
              </div>
            )}

            {selectedNote && noteContent && (
              <div className="space-y-4">
                {/* Header bar */}
                <div className="flex flex-wrap justify-between items-start gap-2">
                  <div>
                    <h3 className="text-lg font-bold text-emerald-800">{noteContent.title || selectedNote.title}</h3>
                    <p className="text-sm text-muted-foreground">{selectedNote.subject} — Year {selectedNote.year_level}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => toggleShare(selectedNote.id)} data-testid="toggle-share-btn">
                      {selectedNote.shared ? <><Check className="mr-1 h-4 w-4 text-green-600" /> Shared with Students</> : <><Share2 className="mr-1 h-4 w-4" /> Share to Students</>}
                    </Button>
                    <Button variant="outline" size="sm" onClick={printNote} data-testid="print-note-btn">
                      <Printer className="mr-1 h-4 w-4" /> Print
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDownloadPptx(selectedNote.id, selectedNote.title)} data-testid="download-pptx-btn">
                      <Download className="mr-1 h-4 w-4" /> PPTX
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedNote(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Summary */}
                {noteContent.summary && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-sm italic text-emerald-800">{noteContent.summary}</p>
                  </div>
                )}

                {/* Sections with images */}
                {(noteContent.sections || []).map((section, idx) => (
                  <div key={idx} className="border rounded-lg p-4">
                    <h4 className="font-semibold text-emerald-700 mb-2">{section.heading}</h4>
                    <p className="text-sm text-gray-700 mb-2 whitespace-pre-line">{section.content}</p>

                    {section.key_terms && section.key_terms.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        {section.key_terms.map((t, i) => (
                          <span key={i} className="bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full text-xs font-medium">{t}</span>
                        ))}
                      </div>
                    )}

                    {/* AI Generated Image */}
                    {noteContent.images && noteContent.images[String(idx)] && (
                      <div className="bg-gray-50 border rounded-lg p-3 mt-2 text-center">
                        <img
                          src={`data:image/png;base64,${noteContent.images[String(idx)]}`}
                          alt={section.image_description || 'AI Diagram'}
                          className="max-w-full max-h-[400px] mx-auto rounded-lg"
                        />
                        {section.image_description && (
                          <p className="text-xs text-muted-foreground italic mt-2">Figure: {section.image_description}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Practice Questions */}
                {noteContent.practice_questions && noteContent.practice_questions.length > 0 && (
                  <div className="border rounded-lg p-4 bg-amber-50/50">
                    <h4 className="font-semibold text-amber-800 mb-3">Practice Questions</h4>
                    {noteContent.practice_questions.map((q, j) => (
                      <div key={j} className="mb-3">
                        <p className="text-sm font-medium">Q{j+1}. {q.question}</p>
                        <p className="text-sm text-emerald-700 mt-1 pl-4 border-l-2 border-emerald-200">A: {q.answer}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
